import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatCurrency, formatDate } from '../../../utils/localization';

const SettlementHistory = ({ settlements = [], onSettlementDelete }) => {
  const settlementMethodLabels = {
    cash: 'Cash',
    venmo: 'Venmo',
    paypal: 'PayPal',
    zelle: 'Zelle',
    bank_transfer: 'Bank Transfer',
    other: 'Other'
  };

  if (settlements.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="DollarSign" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No settlements yet</h3>
        <p className="text-muted-foreground">Record payments between participants to track settlement history.</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Settlement History</h2>
      </div>
      <div className="overflow-x-auto">
        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">From</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">To</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Method</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((settlement, index) => (
                <tr
                  key={settlement.id}
                  className={`border-b border-border ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-foreground">
                          {settlement.payer?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-foreground">{settlement.payer?.name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-success-foreground">
                          {settlement.payee?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-foreground">{settlement.payee?.name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="font-semibold text-foreground">{formatCurrency(settlement.amount)}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">
                      {settlementMethodLabels[settlement.method] || settlement.method}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">{formatDate(settlement.date)}</span>
                  </td>
                  <td className="p-4 text-right">
                    {onSettlementDelete && (
                      <button
                        onClick={() => onSettlementDelete(settlement)}
                        className="text-destructive hover:text-destructive/80 transition-micro"
                        title="Delete settlement"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-border">
          {settlements.map((settlement) => (
            <div key={settlement.id} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-primary-foreground">
                      {settlement.payer?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="font-medium text-foreground">{settlement.payer?.name}</span>
                </div>
                <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-success-foreground">
                      {settlement.payee?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="font-medium text-foreground">{settlement.payee?.name}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="font-semibold text-foreground">{formatCurrency(settlement.amount)}</span>
                <span className="text-xs text-muted-foreground">
                  {settlementMethodLabels[settlement.method] || settlement.method}
                </span>
              </div>
              
              <div className="mt-2 text-xs text-muted-foreground">
                {formatDate(settlement.date)}
              </div>
              
              {onSettlementDelete && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => onSettlementDelete(settlement)}
                    className="text-destructive hover:text-destructive/80 transition-micro text-sm"
                    title="Delete settlement"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettlementHistory;
